import React from 'react'
import styled from 'styled-components'

const Container = styled.div`
  min-height: 100vh;
  padding: 6rem var(--container-padding-desktop) 4rem;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );

  @media (max-width: 1024px) {
    padding: 6rem var(--container-padding-tablet) 4rem;
  }

  @media (max-width: 768px) {
    padding: 5rem var(--container-padding-mobile) 3rem;
  }
`

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 2.5rem);
  color: var(--color-primary);
  margin-bottom: 2rem;
`

const Section = styled.section`
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--color-text);
  margin-bottom: 1rem;
`

const Text = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const List = styled.ul`
  list-style-type: disc;
  color: var(--color-text-secondary);
  margin: 1rem 0 1rem 1.5rem;
  line-height: 1.8;

  li {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const LastUpdated = styled.p`
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  margin-bottom: 2rem;
`

export default function Terms() {
  return (
    <Container>
      <Content>
        <Title>Terms of Service</Title>
        <LastUpdated>Last Updated: February 15, 2024</LastUpdated>

        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <Text>
            By accessing and using OmniBrain's services, you agree to be bound by these Terms of Service
            and all applicable laws and regulations. If you do not agree with any of these terms, you
            are prohibited from using or accessing our services.
          </Text>
        </Section>

        <Section>
          <SectionTitle>2. Use of Services</SectionTitle>
          <Text>
            Our services are designed to facilitate AI-powered project development and collaboration.
            You agree to use these services only for lawful purposes and in accordance with these Terms.
          </Text>
          <List>
            <li>You must maintain the security of your wallet and credentials</li>
            <li>You are responsible for all activity that occurs under your account</li>
            <li>You must not use the services to violate any laws or regulations</li>
            <li>You must not interfere with or disrupt the services or servers</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Intellectual Property</SectionTitle>
          <Text>
            You retain ownership of any intellectual property rights to content you submit through our
            services. By using our services, you grant us a worldwide, non-exclusive license to use,
            store, and process your content solely for the purpose of providing our services.
          </Text>
        </Section>

        <Section>
          <SectionTitle>4. AI Agents and Generated Content</SectionTitle>
          <Text>
            Our AI agents generate content based on your inputs and requirements. While we strive for
            high quality, we do not guarantee that the generated content will be error-free or meet
            your specific needs. You are responsible for reviewing and validating any generated content
            before use.
          </Text>
        </Section>

        <Section>
          <SectionTitle>5. Blockchain Integration</SectionTitle>
          <Text>
            Our services integrate with blockchain technology. You acknowledge that:
          </Text>
          <List>
            <li>Blockchain transactions are irreversible</li>
            <li>You are responsible for your wallet security</li>
            <li>Network fees may apply to transactions</li>
            <li>Market conditions may affect token values</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Limitation of Liability</SectionTitle>
          <Text>
            OmniBrain and its affiliates shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages, including without limitation, loss of profits, data,
            use, goodwill, or other intangible losses, resulting from your access to or use of or
            inability to access or use the services.
          </Text>
        </Section>

        <Section>
          <SectionTitle>7. Modifications to Service</SectionTitle>
          <Text>
            We reserve the right to modify or discontinue, temporarily or permanently, the service
            with or without notice. We shall not be liable to you or to any third party for any
            modification, suspension, or discontinuance of the service.
          </Text>
        </Section>

        <Section>
          <SectionTitle>8. Governing Law</SectionTitle>
          <Text>
            These Terms shall be governed by and construed in accordance with the laws of the
            jurisdiction in which OmniBrain operates, without regard to its conflict of law provisions.
          </Text>
        </Section>

        <Section>
          <SectionTitle>9. Contact Information</SectionTitle>
          <Text>
            For any questions about these Terms of Service, please contact us at legal@omnibrain.ai
          </Text>
        </Section>
      </Content>
    </Container>
  )
}
